import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Popconfirm, message, Typography, Space, Descriptions, Modal, Input } from 'antd';
import { DeleteOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useGetCustomToursMutation, useDeleteCustomTourMutation } from '../redux/services/apiSlice';

const { Title } = Typography;

const CustomTours = () => {
    const [getCustomTours, { data, isLoading }] = useGetCustomToursMutation();
    const [deleteCustomTour] = useDeleteCustomTourMutation();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [searchText, setSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState('-createdAt');

    const fetchCustomTours = useCallback((page = 1, limit = 10, search = '', sort = '-createdAt') => {
        getCustomTours({ page, limit, search, sort });
    }, [getCustomTours]);

    useEffect(() => {
        fetchCustomTours(pagination.current, pagination.pageSize, searchText, sortOrder);
    }, [fetchCustomTours, pagination.current, pagination.pageSize, searchText, sortOrder]);

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
        if (sorter.field) {
            const order = sorter.order === 'ascend' ? sorter.field : `-${sorter.field}`;
            setSortOrder(order);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination({ ...pagination, current: 1 });
    };

    const handleDelete = async (id) => {
        try {
            await deleteCustomTour(id).unwrap();
            message.success('Custom tour deleted');
            fetchCustomTours(pagination.current, pagination.pageSize, searchText, sortOrder);
        } catch (error) {
            message.error('Error deleting');
        }
    };

    const showDetails = (record) => {
        Modal.info({
            title: 'Custom Tour Details',
            width: 600,
            content: (
                <Descriptions bordered column={1}>
                    <Descriptions.Item label="Customer Name">{record.customerName}</Descriptions.Item>
                    <Descriptions.Item label="Email">{record.email}</Descriptions.Item>
                    <Descriptions.Item label="Duration">{record.tourDuration}</Descriptions.Item>
                    <Descriptions.Item label="Start Date">{new Date(record.startDate).toLocaleDateString()}</Descriptions.Item>
                    <Descriptions.Item label="End Date">{new Date(record.endDate).toLocaleDateString()}</Descriptions.Item>
                    <Descriptions.Item label="Start From">{record.startFrom}</Descriptions.Item>
                    <Descriptions.Item label="Destination">{record.destination}</Descriptions.Item>
                </Descriptions>
            ),
        });
    };

    const columns = [
        { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: (date) => new Date(date).toLocaleDateString(), sorter: true },
        { title: 'Customer', dataIndex: 'customerName', key: 'customerName', sorter: true },
        { title: 'Destination', dataIndex: 'destination', key: 'destination', sorter: true },
        { title: 'Duration', dataIndex: 'tourDuration', key: 'tourDuration' },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => showDetails(record)} size="small" />
                    <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record._id)}>
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Custom Tour Requests</Title>
                <Input.Search
                    placeholder="Search custom tours..."
                    onSearch={handleSearch}
                    style={{ width: 300 }}
                    enterButton={<SearchOutlined />}
                />
            </div>
            <Table 
                dataSource={data?.tours || []} 
                columns={columns} 
                rowKey="_id" 
                loading={isLoading}
                pagination={{
                    current: data?.currentPage || 1,
                    pageSize: pagination.pageSize,
                    total: data?.totalItems || 0,
                    showSizeChanger: true
                }}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default CustomTours;
